import * as R from 'ramda';
import {parseISO} from 'date-fns';
import {camelCase} from '@practicegenius/pg-util/src/StringUtils';
import {UnknownServerData} from '@practicegenius/http-clients-js/src/types';
import {formatDateRange} from '@practicegenius/pg-util/src/DateUtils';
import {objectKeysToCamelCaseDeep} from '@practicegenius/pg-util/src/ObjectUtils';
import {setType} from '@practicegenius/pg-util/src/TypescriptUtils';
import {Address} from '../address';
import {RelativeSchedule} from '../relativeSchedule';

export enum PromotionStatus {
  needs_action = 'needs_action',
  published = 'published',
  scheduled = 'scheduled',
  draft = 'draft',
  past = 'past',
}

// TODO can should extend PromotionStatus?
export enum PromotionFilterStatus {
  needs_action = 'needs_action',
  published = 'published',
  scheduled = 'scheduled',
  draft = 'draft',
  past = 'past',
  all = 'all',
}

export enum PromotionType {
  event = 'event',
  offer = 'offer',
  giveaway = 'giveaway',
}

export enum PromotionField {
  name = 'name',
  title = 'title',
  location = 'location',
  locationData = 'locationData',
  locationType = 'locationType',
  locationDataLink = 'locationDataLink',
  locationDataName = 'locationDataName',
  locationDataAddressLine1 = 'locationDataAddressLine1',
  locationDataAddressLine2 = 'locationDataAddressLine2',
  locationDataCity = 'locationDataCity',
  locationDataState = 'locationDataState',
  locationDataZip = 'locationDataZip',
  locationDataCountry = 'locationDataCountry',
  locationDataIds = 'locationDataIds',
  startDate = 'startDate',
  endDate = 'endDate',
  audience = 'audience',
  userGroupIds = 'userGroupIds',
  image = 'image',
  shortDescription = 'shortDescription',
  longDescription = 'longDescription',
  publish = 'publish',
  relativeScheduleUnit = 'relativeScheduleUnit',
  relativeScheduleInterval = 'relativeScheduleInterval',
  giveawayRewardWinnerPoints = 'giveawayRewardWinnerPoints',
  giveawayRewardWinnerInHouseReward = 'giveawayRewardWinnerInHouseReward',
  giveawayRewardParticipantPoints = 'giveawayRewardParticipantPoints',
  giveawayEntryActionsRedeemForEntriesPoints = 'giveawayEntryActionsRedeemForEntriesPoints',
  giveawayEntryActionsRedeemForEntriesTitle = 'giveawayEntryActionsRedeemForEntriesTitle',
  giveawayEntryActionsDailyEntrySpinPossibleBonusEntries = 'giveawayEntryActionsDailyEntrySpinPossibleBonusEntries',
  giveawayEntryActionsDailyEntrySpinTitle = 'giveawayEntryActionsDailyEntrySpinTitle',
}

export enum PromotionLocationType {
  venue = 'venue',
  office = 'office',
  online = 'online',
  toBeDetermined = 'toBeDetermined',
  none = 'none',
}

export type Geometry = {
  location: google.maps.LatLng | google.maps.LatLngLiteral;
  viewport: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral;
};

export enum PromotionAudienceType {
  public = 'public',
  members = 'members',
}

export type PromotionAudience =
  | {audience: PromotionAudienceType.public}
  | {
      audience: PromotionAudienceType.members;
      userGroupIds: number[];
    };

export enum PromotionPublishType {
  now = 'now',
  startDate = 'startDate',
  relativeSchedule = 'relativeSchedule',
}

export type PromotionPublish =
  | {
      publish: PromotionPublishType.now;
    }
  | {
      publish: PromotionPublishType.startDate;
    }
  | {
      publish: PromotionPublishType.relativeSchedule;
      relativeSchedule: RelativeSchedule;
    };

export type PromotionLocationVenue = {
  name: string;
  geometry?: Geometry;
} & Address;

export type PromotionLocation =
  | {
      type: PromotionLocationType.venue;
      data: PromotionLocationVenue | null;
    }
  | {
      type: PromotionLocationType.office;
      data: {
        ids: number[];
      };
    }
  | {type: PromotionLocationType.online; data: {link: string}}
  | {type: PromotionLocationType.toBeDetermined; data: null}
  | {type: PromotionLocationType.none; data: null};

export type PromotionBonusEntrySettings =
  | {
      type: 'redeem_for_entries';
      title: string;
      points: number;
    }
  | {
      type: 'daily_entry_spin';
      title: string;
      possibleBonusEntries: number[];
      theme: string;
    };

type GiveawayHasWinner =
  | {
      hasWinner: true;
      winnerId: number;
    }
  | {
      hasWinner: false;
    };

export type GiveawayDetails = GiveawayHasWinner & {
  reward: PromotionRewardSettings;
  showTotalParticipantEntries: boolean;
  entryActions?: PromotionBonusEntrySettings[];
  winnerReveal: {type: 'spin_wheel'; theme: string};
};

type BasePromotion = PromotionPublish &
  PromotionAudience & {
    id: number;
    name: string;
    title: string;
    image: string;
    startDate: Date;
    endDate: Date;
    shortDescription: string;
    longDescription: string;
    url: string;
    hashId: string;
    status: PromotionStatus;
    settings: PromotionSettings;
  };

export type PublishedPromotion = BasePromotion & {
  draft: false;
} & (
    | {
        type: PromotionType.event;
        location: PromotionLocation;
      }
    | {type: PromotionType.offer}
    | {
        type: PromotionType.giveaway;
        giveaway: GiveawayDetails;
        participantsCount: number;
        winnerName?: string;
      }
  );

export type UnpublishedPromotion = BasePromotion & {
  draft: true;
  type: PromotionType;
  location?: PromotionLocation;
  giveaway?: GiveawayDetails;
};

export type PromotionSettings = {
  colors: {
    bodyAccent: string;
    bodyBackground: string;
    bodyText: string;
    headlineBackground: string;
    headlineText: string;
  };
  startAndEndTimes: {
    showDates: boolean;
    // Technically, this is defined when PromotionType.Event and undefined when PromotionType.Offer,
    // but I don't think it's worth the effort to model that detail at this time.
    showTimes?: boolean;
  };
};

export type PromotionParticipantTypes = 'winner' | 'participant';
export type PromotionRewardsWinner =
  | {
      type: 'points';
      points: number;
    }
  | {
      type: 'in_house_reward';
      inHouseReward: string;
    };
export type PromotionRewardsParticipant =
  | {
      type: 'points';
      points: number;
    }
  | {
      type: 'no_reward';
    };

export type PromotionRewardSettings = {
  winner: PromotionRewardsWinner;
  participant: PromotionRewardsParticipant;
};

export type Promotion = PublishedPromotion | UnpublishedPromotion;

export type PromotionListItem = {
  id: number;
  type: PromotionType;
  status: PromotionStatus;
  name: string;
  title: string;
  shortDescription: string;
  longDescription: string;
  url: string | null;
  color: string | null;
  image: string;
  startDate: Date;
  endDate: Date;

  /** Location is undefined when type: Offer;
   * Location may be null when status: Draft */
  location?: PromotionLocation | null;
  settings: PromotionSettings;

  /** only present if promotion type is giveaway */
  participantsCount?: number;

  /** only present if audience === PromotionAudienceType.members, but
   * we don't have audience here */
  recipientsCount?: number;

  /** only present if promotion type is giveaway */
  giveaway?: GiveawayDetails;

  /** only present if promotion type is giveaway */
  winnerName?: string;
};

export type DailySpinEntriesType =
  | {
      participated: false;
    }
  | {
      participated: true;
      spinResult: number;
    };

export type PromotionGiveawayEntries = {
  authUserEntries: {
    initialEntries: boolean;
    dailyActivity: {
      spinForEntries: DailySpinEntriesType;
    };
    totalEntriesPerActivity: {
      redeemForEntries: number;
      spinEntries: number;
    };
  };
  entriesCount: number;
};

export type UserRevealStatus =
  | {
      type: 'unrevealed';
    }
  | {
      type: 'revealed';
      currentUserIsWinner: boolean;
    };

export type UserWinnerRevealedStatus = Extract<
  UserRevealStatus,
  {type: 'revealed'}
>;

const renameKeys = R.curry((keysMap, obj) =>
  R.reduce(
    (acc, key) => R.assoc(keysMap[key] || key, obj[key], acc),
    {},
    R.keys(obj)
  )
);

/**
 * Camelcases the necessary values when transforming from server
 */
const camelcaseValues = R.evolve({
  location: {
    type: camelCase,
  },
  publish: camelCase,
});

/**
 * Transforms incoming entry actions to array from object
 */
export const transformIncomingEntryActions = R.evolve({
  giveaway: {
    entryActions: Object.values,
  },
});

/**
 * Transforms incoming participant reward to remove points if 'no_reward'
 */
export const transformIncomingParticipantReward = R.evolve({
  giveaway: {
    reward: {
      participant: R.when(R.propEq('no_reward', 'type'), R.dissoc('points')),
    },
  },
});

/**
 * Transforms incoming giveaway winner id
 */
export const transformIncomingGiveawayWinnerId = R.evolve({
  giveaway: renameKeys({winner: 'winnerId'}),
});

/**
 * Date-ifys incoming dates
 */
export const transformIncomingPromotionDates = R.evolve({
  startDate: R.unless(R.either(R.isNil, R.isEmpty), parseISO),
  endDate: R.unless(R.either(R.isNil, R.isEmpty), parseISO),
});

export const transformIncomingPromotion = R.pipe(
  objectKeysToCamelCaseDeep,
  // @ts-ignore
  camelcaseValues,
  transformIncomingPromotionDates,
  transformIncomingEntryActions,
  transformIncomingParticipantReward,
  transformIncomingGiveawayWinnerId
);

export const promotionsResponseToListItems: (
  data: UnknownServerData
) => PromotionListItem[] = R.pipe(
  R.propOr([], 'items'),
  R.map(transformIncomingPromotion),
  setType<PromotionListItem[]>()
);

export const transformPromotionResponse: (
  data: UnknownServerData
) => Promotion = R.pipe(
  R.propOr({}, 'item'),
  transformIncomingPromotion,
  setType<Promotion>()
);

export const getPromotionFormattedDateRange = (
  promotion: Pick<Promotion, 'startDate' | 'endDate' | 'settings'>
): string =>
  R.pathEq(true, ['settings', 'startAndEndTimes', 'showDates'], promotion)
    ? formatDateRange(promotion.startDate, promotion.endDate)
    : '';
